import { apiClient } from './api/client';
import {
  Employee,
  UpdateMePayload,
} from '@/types/employees';
import type { AppRole } from '@/types/auth';

const API_BASE = '/employees';

export const employeesService = {
  /**
   * Lista os employees acessíveis ao usuário autenticado.
   * `includeInactive` só na tela de admin de usuários (sem ele um interno
   * desativado some da lista e não teria como ser reativado).
   */
  async getManaged(includeInactive = false): Promise<Employee[]> {
    const response = await apiClient.get<Employee[]>(`${API_BASE}/managed`, {
      params: includeInactive ? { include_inactive: 'true' } : undefined,
    });
    return response.data || [];
  },

  /**
   * Lista employees ativos do CC com CPF válido (para selector de analista SIGRA).
   */
  async getByCostCenterWithCpf(ccId: number): Promise<Employee[]> {
    const response = await apiClient.get<Employee[]>(`${API_BASE}/by-cost-center/${ccId}`);
    return response.data || [];
  },

  /**
   * Employees ativos do CC (sem filtro de CPF), opcionalmente de uma role só.
   */
  async getActiveByCostCenter(
    ccId: number,
    role?: AppRole,
  ): Promise<Employee[]> {
    const response = await apiClient.get<Employee[]>(
      `${API_BASE}/active-by-cost-center/${ccId}`,
      role ? { params: { role } } : undefined,
    );
    return response.data || [];
  },

  /** Internos ativos da EMPRESA com CPF válido (selector de analista do onboarding). */
  async getByCompanyWithCpf(companyId: number): Promise<Employee[]> {
    const response = await apiClient.get<Employee[]>(
      `${API_BASE}/by-company/${companyId}`,
    );
    return response.data || [];
  },

  /** Internos ativos com vínculo operacional na EMPRESA (T7b). */
  async getActiveByCompany(companyId: number, role?: string): Promise<Employee[]> {
    const response = await apiClient.get<Employee[]>(
      `${API_BASE}/active-by-company/${companyId}`,
      role ? { params: { role } } : undefined,
    );
    return response.data || [];
  },

  /**
   * Analistas atendentes da EMPRESA. Mesmo recorte do
   * `getAnalistasByCostCenter`, no eixo que sobrou: `process_types` perdeu o
   * `cost_center_id`, então o seletor só consegue perguntar pela empresa.
   */
  async getAnalistasByCompany(companyId: number): Promise<Employee[]> {
    return this.getActiveByCompany(companyId, 'Analista');
  },

  /**
   * Analistas atendentes do CC — quem pode ser vinculado a uma empresa ou a um
   * tipo de processo. CPF não entra: só o SIGRA precisa dele.
   */
  async getAnalistasByCostCenter(ccId: number): Promise<Employee[]> {
    return this.getActiveByCostCenter(ccId, 'Analista');
  },

  /**
   * Busca um employee por ID
   */
  async getById(id: number): Promise<Employee> {
    const response = await apiClient.get<Employee>(`${API_BASE}/${id}`);
    return response.data;
  },

  async updateMe(payload: UpdateMePayload): Promise<void> {
    await apiClient.put(`${API_BASE}/me`, payload);
  },

  async getRoles(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${API_BASE}/roles`);
    return response.data || [];
  },
};

